import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Code, ShoppingCart, TrendingUp, ShieldCheck } from "lucide-react";
import { usePartnerViewed } from "@/utils/trackingHooks";
import SEOHead from "@/components/SEOHead";
import EcomPartnerIncomeCalculator from "@/components/EcomPartnerIncomeCalculator";
import LeadForm from "@/forms/engine/LeadForm";
import partnerEnquiry, { partnerCalculatorContext } from "@/forms/definitions/partnerEnquiry";
import TrustBar from "@/components/TrustBar";
import RelatedTools from "@/components/RelatedTools";
import SolutionsCrossSell from "@/components/SolutionsCrossSell";
import { fadeUp, stagger, viewportOnce } from "@/utils/motionVariants";

export default function EcomPartnerCalculatorPage() {
  const [calculator, setCalculator] = useState(null);
  usePartnerViewed("ecommerce_partner_calculator", { itemType: "partner_calculator" });
  return (
    <div>
      <SEOHead title="E-Commerce Partner Calculator" description="Maximise your revenue potential with our e-commerce partner calculator. Discover your commission rates for referring online businesses to CanDo Payments." path="/ecom-partner-calculator" />

      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">E-commerce Partners</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">Earn recurring income from <span className="gradient-text">every integration</span></motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">If you build, manage or consult on e-commerce stores, you already know merchants paying too much for payments. Refer them to CanDo and earn starting from 20% of the margin — every month, for as long as they process.</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
              <a href="#calculator" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors btn-shine">Calculate Your Income <ArrowRight size={16} /></a>
              <Link to="/partners/integrations" className="bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 hover:bg-brand-aqua-light transition-colors">View Integrations</Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <TrustBar />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-12">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Built for Developers & Agencies</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Why e-commerce partners choose CanDo</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Code, title: "Plug-and-play integrations", desc: "WooCommerce, Shopify, Magento, PrestaShop, BigCommerce, OpenCart and more. Install in minutes, not days." },
              { icon: TrendingUp, title: "Starting from 20% recurring commission", desc: "Earn starting from 20% of the gross margin on every merchant you refer — paid every month, not just a one-off bonus." },
              { icon: ShieldCheck, title: "Enterprise-grade processing", desc: "Powered by Elavon and U.S. Bank. PCI compliant, 3-D Secure, tokenisation, and next-day settlement as standard." },
            ].map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6">
                <item.icon size={24} className="text-brand-aqua mb-4" />
                <h3 className="font-heading font-bold text-brand-black mb-2">{item.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="calculator" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">How much could you earn?</h2>
              <p className="text-brand-grey leading-relaxed mb-6">Most developers and agencies already manage merchants who could save on payment processing. This calculator shows you what that relationship is worth in recurring income.</p>
              <div className="space-y-4 mb-8">
                {[
                  { stat: "starting from 20%", desc: "of gross margin — paid to you every month" },
                  { stat: "7+", desc: "plug-and-play e-commerce platform integrations" },
                  { stat: "£0", desc: "to join. No fees, no risk, no lock-in" },
                ].map((item) => (
                  <div key={item.stat} className="flex items-start gap-3">
                    <span className="font-heading text-lg font-bold text-brand-aqua-dark flex-shrink-0 w-44">{item.stat}</span>
                    <span className="text-sm text-brand-grey">{item.desc}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white border border-brand-light-grey rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <ShoppingCart size={20} className="text-brand-aqua flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-brand-black">Already building on a supported platform?</p>
                    <p className="text-xs text-brand-grey mt-0.5"><Link to="/partners/integrations" className="text-brand-aqua font-semibold">View all integrations</Link> or <a href="/book" className="text-brand-aqua font-semibold">book a partner call</a>.</p>
                  </div>
                </div>
              </div>
            </div>
            <EcomPartnerIncomeCalculator onChange={setCalculator} />
          </div>
        </div>
      </section>

      <RelatedTools currentPath="/ecom-partner-calculator" />
      <SolutionsCrossSell currentPath="/ecom-partner-calculator" />

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
            <h3 className="font-heading text-xl font-bold text-brand-black mb-2">Ready to start earning?</h3>
            <p className="text-sm text-brand-grey mb-6">Register your interest and we&apos;ll get you set up with sandbox access, documentation, and a dedicated partner manager.</p>
            <LeadForm schema={partnerEnquiry} context={partnerCalculatorContext("E-commerce", calculator)} />
          </div>
        </div>
      </section>
    </div>
  );
}
